import { Alert, Box, Button, LinearProgress, Typography } from '@mui/material'
import { useMutation, useQuery } from 'react-query'
import SpreadsheetService from '../../../APIs/Python/APIs/SpreadsheetService'
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import * as React from 'react'
import { useEffect, useState } from 'react'
import EntryStepsHeader from './EntryStepsHeader'
import { AxiosResponse } from 'axios'
import useInterval from '../../../Functions/SourcedOnline/useInterval'
import { SpreadsheetStatus } from './DataEntry'
import { Undo } from '@mui/icons-material'
import { useUserContext } from '../../../Contexts/UserConfig'

function CompleteEntry() {
  const { study_id } = useParams()
  const { whoAmI } = useUserContext()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const id = searchParams.get('id')

  const [error, setError] = useState<AxiosResponse | undefined>(undefined)
  const [poll, setPoll] = useState(false)

  //STEP 1
  const { mutate: updateSpreadsheet, isLoading: loadingClose } = useMutation<
    any,
    AxiosResponse
  >(() => SpreadsheetService.updateSpreadsheet(whoAmI, study_id, id!), {
    onSuccess: () => setPoll(true),
    onError: (data) => setError(data),
  })

  useEffect(() => {
    if (id) {
      updateSpreadsheet()
    }
  }, [id, updateSpreadsheet])

  //STEP 2
  const { data: spreadsheet, refetch } = useQuery<
    SpreadsheetStatus,
    AxiosResponse
  >(
    ['SpreadsheetService', 'checkSpreadsheet', study_id, id],
    () => SpreadsheetService.checkSpreadsheet(whoAmI, study_id, id!),
    {
      enabled: !!whoAmI && !!study_id && !!id,
      onError: (data) => setError(data),
    },
  )

  const maxPolls = 30 * 5
  const secondInterval = 2
  const [pollsRemain, setPollsRemain] = useState(maxPolls)

  useInterval(async () => {
    if (
      !error &&
      id &&
      poll &&
      pollsRemain > 0 &&
      spreadsheet?.Status !== 'FIN'
    ) {
      setPollsRemain((t) => t - 1)
      await refetch()
    }
  }, secondInterval * 1000) // to seconds

  if (location.pathname.endsWith('/complete') && !id) {
    return (
      <Navigate
        to={`../select`}
        replace
      />
    )
  }
  return (
    <Box
      sx={{
        width: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        mx: 2,
      }}
    >
      <EntryStepsHeader step={2}>
        {spreadsheet && (
          <Box sx={{ mt: 2, width: 1, maxWidth: 'md' }}>
            {(!id || loadingClose || spreadsheet.Status !== 'FIN') &&
              pollsRemain > 0 && <LinearProgress sx={{ width: 1, mb: 1 }} />}
            {pollsRemain > 0 ?
              <Alert
                severity={spreadsheet.Status === 'FIN' ? 'success' : 'info'}
              >
                <Box
                  sx={{
                    minWidth: 160,
                    width: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    p: 1,
                  }}
                >
                  <Box sx={{ display: 'flex' }}>
                    <Typography sx={{ fontWeight: 'bold' }}>
                      {'Status:'}
                    </Typography>
                    &nbsp;&nbsp;
                    <Typography>{spreadsheet.Status}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex' }}>
                    <Typography sx={{ fontWeight: 'bold' }}>
                      {'Message:'}
                    </Typography>
                    &nbsp;&nbsp;
                    <Typography>{spreadsheet.StatusMessage}</Typography>
                  </Box>
                </Box>
              </Alert>
            : <Alert severity={'error'}>
                <Box
                  sx={{
                    minWidth: 160,
                    width: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    p: 1,
                  }}
                >
                  <Box sx={{ display: 'flex' }}>
                    <Typography sx={{ fontWeight: 'bold' }}>
                      {'Timeout:'}
                    </Typography>
                    &nbsp;&nbsp;
                    <Typography>{`The spreadsheet is taking too long to save. The UI has reached its polling limit of ${maxPolls * secondInterval}s`}</Typography>
                  </Box>
                </Box>
              </Alert>
            }
          </Box>
        )}
        {error && (
          <Alert severity={'error'}>
            <Box
              sx={{
                minWidth: 160,
                width: 1,
                display: 'flex',
                flexDirection: 'column',
                p: 1,
              }}
            >
              <Box sx={{ display: 'flex' }}>
                <Typography sx={{ fontWeight: 'bold' }}>{'Status:'}</Typography>
                &nbsp;&nbsp;
                <Typography>{error?.status}</Typography>
              </Box>
              <Box sx={{ display: 'flex' }}>
                <Typography sx={{ fontWeight: 'bold' }}>
                  {'Message:'}
                </Typography>
                &nbsp;&nbsp;
                <Typography>{error?.statusText}</Typography>
              </Box>
            </Box>
          </Alert>
        )}
        <Button
          variant={'outlined'}
          onClick={() => {
            navigate('../select')
          }}
          sx={{ m: 2 }}
          startIcon={<Undo />}
          disabled={spreadsheet && spreadsheet.Status !== 'FIN'}
        >
          Return to Animal Selection
        </Button>
      </EntryStepsHeader>
    </Box>
  )
}

export default CompleteEntry
